// eventHandler.js
import {
  createNewGuess,
  addLevelToGame,
  createNewGame,
  finalizeGame
} from './gameData';

import {
  medalMap,
  getFeedbackForGuess,
  buildSingleBlock,
  formatShareableString,
  debugLog
} from './utils';

import {
  getServerCodes,
  getDailyCodes
} from "./dailyCodes";

const getGameKey = (game) => {
  if (!game) return null;
  return game.dateUTC || game.dateISO || null;
};

const upsertHistory = (history, game) => {
  debugLog('[upsertHistory] called with:', history, game);
  if (!Array.isArray(history)) return [game];
  const key = getGameKey(game);
  if (key) {
    const idx = history.findIndex(g => getGameKey(g) === key);
    if (idx !== -1) {
      const next = history.slice();
      next[idx] = game;
      return next;
    }
  }
  if (history.some(g => g.timestamp === game.timestamp)) return history;
  return [...history, game];
};

export function handleKeyPress(digit, guesses, currentRow, setGuesses, gameOver) {
  if (gameOver) return;
  const row = guesses[currentRow];
  if (row.length >= 5) return;
  const updatedRow = [...row, digit.toUpperCase()];
  const updatedGuesses = [...guesses];
  updatedGuesses[currentRow] = updatedRow;
  setGuesses(updatedGuesses);
}

/**
 * Submit the current guess row.
 * Assumes the game was initialized on the splash (currentGame may still be null on true first-ever guess).
 */
export function handleSubmit({
  // Props / state
  hardMode,
  level,                 // 0,1,2 (current level index)
  maxLevel,              // 2 for 3 levels total
  answer,                // STRING like "0A3F9"
  gameOver,
  guesses,               // UI matrix: string[][] (each row is array of chars)
  feedback,              // UI matrix: ('green'|'yellow'|'gray'|null)[][]
  lockedGreens,          // string[] length 5 (for hard mode locks)
  currentRow,

  // Setters
  setCurrentRow,
  setFeedback,
  setCurrentGame,
  setDidWin,
  setGameOver,
  setLockedGreens,
  setGameOn,             // if you still use this to “lock” hardMode after first guess
  setGameHistory,        // you used this previously; keep if you show a session list
  // Game-data helpers passed from App (if you still pass them)
  addGuessToLevel,       // optional: if you still pass this from App; otherwise we write directly
  finalizeLevel,         // required
  currentGame,           // current game object
}) {
  if (gameOver) return;

  const rowArr = guesses[currentRow] || [];
  if (rowArr.length !== 5) return; // incomplete guess

  const guessStr = rowArr.join('').toUpperCase();
  const answerStr = String(answer).toUpperCase();

  // 0) Hard Mode Validation
  // If hardMode is on, ensure every locked green from previous rights is respected
  if (hardMode && lockedGreens) {
    for (let i = 0; i < 5; i++) {
      const lockedChar = lockedGreens[i];
      if (lockedChar && guessStr[i] !== lockedChar) {
        alert(`Hard Mode: You must use '${lockedChar}' in position ${i + 1}.`);
        return; // Block submission
      }
    }

    // Yellow Check: Previous guess yellows must be present in current guess
    if (currentRow > 0) {
      const prevRowIndex = currentRow - 1;
      const prevGuessArr = guesses[prevRowIndex];
      const prevFeedbackArr = feedback[prevRowIndex];

      if (prevGuessArr && prevFeedbackArr) {
        // We will "consume" characters from a copy of the current guess 
        // to handle multiple instances of the same letter correctly.
        const currentGuessPool = [...rowArr];

        for (let i = 0; i < 5; i++) {
          if (prevFeedbackArr[i] === 'yellow') {
            const requiredChar = prevGuessArr[i];
            const foundIndex = currentGuessPool.indexOf(requiredChar);

            if (foundIndex === -1) {
              alert(`Hard Mode: You must include '${requiredChar}' in your guess.`);
              return; // Block submission
            } else {
              // Mark this instance as used so it can't satisfy another requirement
              currentGuessPool[foundIndex] = null;
            }
          }
        }
      }
    }
  }

  // 1) Build feedback for UI (expects arrays)
  const fb = getFeedbackForGuess(guessStr, answerStr); // returns length-5 array
  if (!Array.isArray(fb) || fb.length !== 5) {
    return;
  }

  // 2) Commit feedback to UI
  setFeedback(prev => {
    const next = prev.slice();
    next[currentRow] = fb.slice();
    return next;
  });

  // 3) Mirror into currentGame for history/share
  setCurrentGame(prev => {
    let game = prev;

    // If this is truly the first guess ever and you somehow didn’t init on splash,
    // initialize a minimal game now (no order, server-only flow).
    if (!game) {
      game = addLevelToGame(
        createNewGame(!!hardMode),
        level,
        answerStr,
        !!hardMode
      );
    }

    // Update the current level’s guesses
    const lvlIdx = game.levels.length - 1;
    const lvl = { ...game.levels[lvlIdx] };
    const gArr = Array.isArray(lvl.guesses) ? lvl.guesses.slice() : [];
    gArr[currentRow] = createNewGuess(guessStr, fb.slice());
    lvl.guesses = gArr;

    const levels = game.levels.slice();
    levels[lvlIdx] = lvl;
    return { ...game, levels };
  });

  // 4) Win/lose/continue
  const isRowWin = guessStr === answerStr;
  const rowsAllowed = guesses.length;
  const nextRow = currentRow + 1;
  const isLevelLoss = !isRowWin && nextRow >= rowsAllowed;

  if (isRowWin || isLevelLoss) {
    setGameOver(true);
    setDidWin(isRowWin);

    if (isLevelLoss || (isRowWin && level === maxLevel)) {
      // FULL GAME OVER (Loss or Final Win)
      setCurrentGame(prev => {
        // First, finalize the last level to set completed and crackedOnGuess flags
        const lastLevelIndex = prev.levels.length - 1;
        const lastLevel = prev.levels[lastLevelIndex];
        const finalizedLastLevel = finalizeLevel(lastLevel);

        // Update the levels array with the finalized last level
        const updatedLevels = [...prev.levels.slice(0, -1), finalizedLastLevel];
        const gameWithFinalizedLevel = { ...prev, levels: updatedLevels };

        // Now finalize the entire game
        const final = finalizeGame(gameWithFinalizedLevel, isRowWin);

        if (typeof setGameHistory === 'function') {
          setGameHistory(h => upsertHistory(h, final));
        }
        return final;
      });
    } else {
      // Level won but game continues; finalize level and store partial for medals.
      setCurrentGame(prev => {
        if (!prev) return prev;
        const lastLevelIndex = prev.levels.length - 1;
        const lastLevel = prev.levels[lastLevelIndex];
        const finalizedLastLevel = finalizeLevel(lastLevel);
        const updatedLevels = [...prev.levels.slice(0, -1), finalizedLastLevel];
        const updatedGame = {
          ...prev,
          levels: updatedLevels,
          pendingSkillUp: true,
          gameFinished: false,
        };
        if (typeof setGameHistory === 'function') {
          setGameHistory(h => upsertHistory(h, updatedGame));
        }
        return updatedGame;
      });
    }
    return;
  }

  // Hard mode: lock any new greens for the next row
  if (hardMode) {
    const nextLocks = lockedGreens.slice();
    fb.forEach((color, i) => {
      if (color === 'green') nextLocks[i] = guessStr[i];
    });
    setLockedGreens(nextLocks);
  }

  // First valid submission locks “game on” if you still use that flag
  if (typeof setGameOn === 'function') setGameOn(true);

  // Move to next row
  setCurrentRow(nextRow);
}
//createNewGuess

export async function handleSkillUp({
  currentGame,
  hardMode,
  level,
  maxLevel,
  feedback,
  finalizeLevel,
  buildSingleBlock,
  setShareSummaryBlocks,
  setLevel,
  setAnswer,
  setGuesses,
  setFeedback,
  setCurrentRow,
  setLockedGreens,
  setGameOver,
  setDidWin,
  setCurrentGame,
  TESTMODE,
  sessionDateUTC
}) {

  const nextLevelIndex = level + 1;
  let bundle;
  if (TESTMODE && sessionDateUTC) {
    bundle = await getDailyCodes(sessionDateUTC);
  } else {
    bundle = await getServerCodes(false); // today (cached)
  }
  const meta = bundle.levels[nextLevelIndex];

  // Required:
  let newAnswer = meta.code.toUpperCase();

  // Make the new answer live for the UI/input guards
  setAnswer(newAnswer);

  // ─────────────────────────────────────────────────────────────────────────────
  // Update game state: finalize the previous level, add the new level payload.
  // This preserves your existing finalize/add flow.
  // ─────────────────────────────────────────────────────────────────────────────
  setCurrentGame(prev => {

    const lastLevel = prev.levels[prev.levels.length - 1];
    const finalizedLevel = finalizeLevel(lastLevel);
    const updatedLevelsArray = [...prev.levels.slice(0, -1), finalizedLevel];
    const gameDataReadyForNewLevel = {
      ...prev,
      levels: updatedLevelsArray,
      pendingSkillUp: false,
      gameFinished: false,
    };

    // Add the new level structure for `nextLevelIndex`
    const gameDataWithNewLevelAdded = addLevelToGame(
      gameDataReadyForNewLevel,
      nextLevelIndex,
      newAnswer,
      hardMode
    );

    return gameDataWithNewLevelAdded;
  });


  // ─────────────────────────────────────────────────────────────────────────────
  // Reset per-level UI scaffolding (keep your existing choices here).
  // ─────────────────────────────────────────────────────────────────────────────
  setLevel(nextLevelIndex);
  const guessesAllowed = hardMode ? (nextLevelIndex + 4) : 6;
  setGuesses(Array(guessesAllowed).fill(""));      // or your existing initial guesses shape
  setFeedback(Array(guessesAllowed).fill([]));     // clear feedback rows
  setCurrentRow(0);
  setLockedGreens(new Array(5).fill(false));
  setGameOver(false);
  setDidWin(false);

}

export function handleDelete(guesses, currentRow, setGuesses, gameOver) {
  if (gameOver) return; // Or if App.js's specific gameOver for the current level is true

  const row = guesses[currentRow];
  if (!row || row.length === 0) return;

  const updatedRow = row.slice(0, -1);
  const updatedGuesses = [...guesses];
  updatedGuesses[currentRow] = updatedRow;
  setGuesses(updatedGuesses);
}

export function handleShare(
  currentGame,
  setShareSummaryBlocks,
  setShareLevelsCleared,
  setShareTotalMaxGuesses,
  setShowShareModal,
  setClipboardMessage
) {
  const latestGame = currentGame;

  if (!latestGame || !latestGame.levels || latestGame.levels.length === 0) {
    setClipboardMessage("Error: No data to share.");
    return;
  }

  const gameLevels = latestGame.levels || [];
  // Generate PLAIN emoji grid strings for each level (for ShareModal display AND clipboard formatting)
  const plainEmojiGridStrings = gameLevels.map((level) => {
    const guesses = level.guesses || [];
    // Make sure feedback exists for each guess for buildSingleBlock
    const feedbackGrid = guesses.map(g => (g && Array.isArray(g.feedback) && g.feedback.length === 5) ? g.feedback : Array(5).fill(null));
    return buildSingleBlock(feedbackGrid);
  });

  // Calculate stats from game data
  // Use crackedOnGuess instead of completed flag for real-time accuracy
  const totalLevelsCleared = gameLevels.filter(level => level.crackedOnGuess !== null && level.crackedOnGuess !== undefined).length;

  // Calculate total guesses used for the clipboard string & score line
  const totalGuessesUsed = plainEmojiGridStrings.reduce((acc, block) => {
    if (block && typeof block === 'string') {
      return acc + block.split('\n').filter(line => line.trim() !== '').length;
    }
    return acc;
  }, 0);

  const totalGuessesAllowed = gameLevels.reduce((sum, level) => sum + (level.guessesAllowed || 0), 0);

  // Use overallWin if available, otherwise fall back to didWin from the root game object
  const didWinGame = latestGame.overallWin === true || (latestGame.overallWin === undefined && latestGame.didWin === true);

  // Generate the final text string for the clipboard using the utility
  const clipboardReadyText = formatShareableString(
    plainEmojiGridStrings,
    totalLevelsCleared,
    totalGuessesUsed,
    totalGuessesAllowed,
    didWinGame,
    medalMap
  );

  // Copy to clipboard directly
  navigator.clipboard.writeText(clipboardReadyText).then(() => {
    setClipboardMessage("Copied to clipboard!");
  }).catch((error) => {
    setClipboardMessage("Failed to copy.");
    console.warn("Clipboard write failed from handleShare:", error);
  });

  // Update App.js state for ShareModal display
  setShareSummaryBlocks(plainEmojiGridStrings);
  setShareLevelsCleared(totalLevelsCleared);
  setShareTotalMaxGuesses(totalGuessesAllowed);
  setShowShareModal(true);
}
